import { useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../services/api';
import { onAngularEvent, trackEvent } from '../../services/angular-bridge';
import { notifySuccess } from '../../services/notify';

export interface BillingCycle {
  totalProjectSize: number;
  processingBytes: number;
  hostingByByteDays: number;
  rollingHostingBytes: number;
  nativeExportsBytes: number;
  imageExportsPageCount: number;
  translatedCharacterCount: number;
  transcriptionSeconds: number;
  dataEnrichmentRequestCount: number;
  glinerNlpDocumentCount: number;
  dailyStatistics: {
    date: number;
    processingBytes: number | null;
    rollingHostingBytes: number | null;
    convertedBytes: number | null;
    nativeExportsBytes: number | null;
    imageExportsPageCount: number | null;
    translatedCharacterCount: number | null;
    transcriptionSeconds: number | null;
    glinerNlpDocumentCount: number | null;
  }[];
  period: {
    startDate: number;
    endDate: number;
  };
}

export function useBilling(projectId: string, startDate?: string, endDate?: string) {
  const path = startDate && endDate
    ? `/billing/project/${projectId}/start/${startDate}/end/${endDate}`
    : `/billing/project/${projectId}`;
  return useQuery<BillingCycle>({
    queryKey: ['billing', projectId, startDate, endDate],
    queryFn: () => api.get<BillingCycle>(path),
    enabled: !!projectId,
  });
}

/**
 * The billing report's completion toast. Billing has a DEDICATED event, so this never
 * competes with the generic ReportComplete consumers in useReportEvents.
 *
 * Mounted by WebSocketBridge rather than by the hook that starts the report: the CSV takes
 * a while to build, and the user is usually off the Billing tab by the time it lands — a
 * listener scoped to that tab would miss its own report.
 *
 * The event's payload is the download URL itself, not an object.
 */
export function useBillingEvents() {
  useEffect(() => {
    return onAngularEvent('FeedService.BillingReportComplete', (url: string) => {
      notifySuccess({
        title: 'Report available',
        description: 'Billing Report has completed.',
        buttons: `<a class="btn btn-primary" href="${url}"><i class="icon-download-alt"></i> Download</a>`,
      });
    });
  }, []);
}

export function useGenerateBillingReport(projectId: string) {
  return useMutation({
    mutationFn: ({ startDate, endDate }: { startDate: string; endDate: string }) => {
      trackEvent('download billing report', 'clicked', `${startDate} to ${endDate}`);
      return api.get(`/billing/csv/project/${projectId}/start/${startDate}/end/${endDate}`);
    },
  });
}

export function billingCsvUrl(projectId: string, startDate: string, endDate: string, baseUrl: string): string {
  return `${baseUrl}/billing/csv/project/${projectId}/start/${startDate}/end/${endDate}`;
}
